import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { addDays, format, startOfDay } from 'date-fns';
import { dayKey } from './calendarEvents';
import OrderEventChip from './OrderEventChip';

export const AGENDA_DAYS = 14;

/**
 * Today, tomorrow, then each day with events for the next two weeks. Mobile-first: one row per booking.
 * Props: buckets, notesSummary, locale, now, undatedCount, onOpenOrder, onShowUndated.
 */
export default function AgendaView({ buckets, notesSummary, locale, now, undatedCount, onOpenOrder, onShowUndated }) {
  const { t } = useTranslation();
  const today = startOfDay(now);
  const groups = Array.from({ length: AGENDA_DAYS + 1 }, (_, i) => addDays(today, i))
    .map((day, i) => ({ day, key: dayKey(day), i, events: buckets[dayKey(day)] || [] }))
    .filter((g) => g.i === 0 || g.events.length > 0);

  const title = (g) => {
    if (g.i === 0) return t('orders.calendar.today');
    if (g.i === 1) return t('orders.calendar.tomorrow');
    return format(g.day, 'EEEE, d MMMM', { locale });
  };

  return (
    <Stack spacing={2}>
      {groups.map((g) => (
        <Box key={g.key} data-testid={`agenda-group-${g.key}`}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {title(g)}
            {g.i <= 1 && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {format(g.day, 'd MMM', { locale })}
              </Typography>
            )}
          </Typography>
          {g.events.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{t('orders.calendar.noEvents')}</Typography>
          ) : (
            <Stack spacing={0.5}>
              {g.events.map((ev) => (
                <OrderEventChip key={ev.id} event={ev} note={notesSummary[ev.id]} onClick={() => onOpenOrder(ev.order)} />
              ))}
            </Stack>
          )}
        </Box>
      ))}
      {undatedCount > 0 && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" variant="outlined" label={t('orders.calendar.undated', { count: undatedCount })} />
          <Button size="small" onClick={onShowUndated}>{t('orders.calendar.showUndated')}</Button>
        </Stack>
      )}
    </Stack>
  );
}
